/**
 * UnlockContentSheetContent
 *
 * Content rendered inside the UnlockContentSheet (via SheetService).
 * Mirrors Flutter's unlock_content_bottom_sheet.dart with three visual states:
 *   1. Access Granted   — user already has access
 *   2. Unlock Disabled  — creator disabled unlock (subscribe to get access)
 *   3. Unlock Available — user can pay coins to unlock
 *
 * Flutter: lib/presentation/bottom_sheet/unlock_content_bottom_sheet.dart
 */
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CTAButtons } from '@/presentation/components/common/sheet/service/components/CTAButtons';
import { SheetCTAFooter } from '@/presentation/components/common/sheet/service/components/SheetCTAFooter';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { useUnlockContent } from '@/presentation/feature/content/hooks/useUnlockContent';
import { UnlockContentPreviewCarousel } from './UnlockContentPreviewCarousel';
import { MathUtils } from '@/domain/utils/mathUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';

interface UnlockContentSheetContentProps {
  media: UserMediaEntity;
  screenKey?: string;
  onClose: () => void;
}

export const UnlockContentSheetContent: React.FC<UnlockContentSheetContentProps> = ({
  media,
  screenKey,
  onClose,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const {
    hasExclusiveRights,
    canUnlock,
    shouldRecharge,
    rechargeAmount,
    isUnlocking,
    executeUnlock,
  } = useUnlockContent(media);

  const handleUnlock = useCallback(async () => {
    const success = await executeUnlock();
    if (success) {
      onClose();
    } else {
      toast.error(l10n.content.contentUnlockError);
    }
  }, [executeUnlock, onClose, l10n]);

  const handleRechargeWallet = useCallback(() => {
    InfoSheet.show({
      title: l10n.content.rechargeWallet,
      infoText: l10n.content.rechargeNotAvailable,
      screenKey,
    });
  }, [l10n, screenKey]);

  const peerName = media.userName ?? '';

  // ── State 1: Access Granted ──────────────────────────────────────────────
  if (hasExclusiveRights) {
    return (
      <div className="flex flex-col" {...qaAttr({ id: 'unlock-content-sheet', status: 'granted' })}>
        <div
          className="flex flex-col"
          style={{
            padding: responsive.spacing['2xl'],
            gap: responsive.spacing.xl,
          }}
        >
          <div className="flex items-center" style={{ gap: responsive.spacing.base }}>
            <AssetIcon
              path="/assets/vectors/unlock-filled.svg"
              width={responsive.icon.medium}
              height={responsive.icon.medium}
              color={theme.icon.primary}
            />
            <span
              style={{
                color: theme.text.primary,
                fontSize: responsive.font.base,
                fontWeight: responsive.fontWeight.semibold,
              }}
            >
              {l10n.content.accessGranted}
            </span>
          </div>

          <span style={{
            // Padding top and bottom to account for small height content
            paddingTop: responsive.spacing['3xl'],
            paddingBottom: responsive.spacing['3xl'],
            color: theme.text.secondary,
            fontSize: responsive.font.sm
          }}>
            {l10n.content.accessGrantedDescription}
          </span>
        </div>

        <SheetCTAFooter>
          <CTAButtons
            cta={{ buttons: [{ text: l10n.common.ok, onClick: onClose, textColor: theme.text.primary, qaId: 'unlock-content-cancel' }] }}
            onClose={() => { }}
          />
        </SheetCTAFooter>
      </div>
    );
  }

  // ── State 2: Unlock Disabled ─────────────────────────────────────────────
  if (!canUnlock) {
    const description = l10n.content.unlockDisabledDescription.replace('{name}', peerName);
    return (
      <div className="flex flex-col" {...qaAttr({ id: 'unlock-content-sheet', status: 'disabled' })}>
        <div
          className="flex flex-col"
          style={{
            padding: responsive.spacing['2xl'],
            gap: responsive.spacing.xl,
          }}
        >
          <span style={{ color: theme.text.secondary, fontSize: responsive.font.sm }}>
            {description}
          </span>
        </div>

        <SheetCTAFooter>
          <CTAButtons
            cta={{ buttons: [{ text: l10n.common.close, onClick: onClose, qaId: 'unlock-content-cancel' }] }}
            onClose={() => { }}
          />
        </SheetCTAFooter>
      </div>
    );
  }

  // ── State 3: Unlock Available ────────────────────────────────────────────
  const unlockCharge = media.unlockCharge ?? 0;
  const coinsText = l10n.content.coinsWillBeCharged.replace('{coins}', String(MathUtils.formatCount(unlockCharge)));
  const unlockDescriptionText = l10n.content.unlockDescription.replace('{name}', peerName);
  const insufficientText = l10n.content.insufficientCoins.replace('{amount}', String(MathUtils.formatCount(rechargeAmount)));

  return (
    <div className="flex flex-col"
      {...qaAttr({ id: 'unlock-content-sheet', status: isUnlocking ? 'unlocking' : 'available' })}
      style={{
        gap: responsive.spacing.xl
      }}>
      {/* Blurred preview carousel */}

      <div style={{ padding: responsive.spacing.xl }}>
        <UnlockContentPreviewCarousel
          media={media}
          style={{
            width: '100%',
            // Cap height at 40dvh while maintaining aspect ratio:
            // maxWidth = maxHeight * aspectRatio ensures width shrinks
            // proportionally instead of using maxHeight (which doesn't
            // reduce width on non-replaced elements like divs)
            maxWidth: `calc(40dvh * ${media.aspectRatio ?? 9 / 16})`,
            aspectRatio: media.aspectRatio ? String(media.aspectRatio) : '9/16',
            margin: '0 auto',
            borderRadius: responsive.borderRadius.base,
            overflow: 'hidden',
          }}
        />
      </div>

      <div
        className="flex flex-col"
        style={{
          padding: responsive.spacing['2xl'],
          paddingTop: 0,
          gap: responsive.spacing.lg,
        }}
      >
        {/* Description */}
        <span style={{ color: theme.text.secondary, fontSize: responsive.font.sm }}>
          {unlockDescriptionText}
        </span>

        {/* Coins charge info */}
        <div
          className="flex items-center"
          {...qaAttr({ id: 'unlock-content-charge', value: unlockCharge })}
          style={{ gap: responsive.spacing.xs }}
        >
          <AssetIcon
            path="/assets/vectors/lock-filled.svg"
            width={responsive.icon.small}
            height={responsive.icon.small}
            color={theme.exclusive}
          />
          <span style={{ color: theme.text.primary, fontSize: responsive.font.sm, fontWeight: responsive.fontWeight.medium }}>
            {coinsText}
          </span>
        </div>

        {/* Insufficient coins warning */}
        {shouldRecharge && (
          <div className="flex flex-col" style={{ gap: responsive.spacing.xs }}>
            <span style={{ color: theme.danger, fontSize: responsive.font.sm }}>
              {insufficientText}
            </span>
            <button
              {...qaAttr({ id: 'unlock-content-recharge' })}
              onClick={handleRechargeWallet}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: theme.blue,
                fontSize: responsive.font.sm,
                fontWeight: responsive.fontWeight.medium,
                textAlign: 'left',
                padding: 0,
              }}
            >
              {l10n.content.rechargeWallet}
            </button>
          </div>
        )}

      </div>

      <SheetCTAFooter>
        <CTAButtons
          cta={{
            buttons: [
              {
                text: l10n.content.unlockVideo,
                onClick: handleUnlock,
                disabled: shouldRecharge || isUnlocking,
                loading: isUnlocking,
                closeOnClick: false,
                textColor: theme.exclusive,
                qaId: 'unlock-content-confirm',
              },
              {
                text: l10n.common.cancel,
                onClick: onClose,
                qaId: 'unlock-content-cancel',
              },
            ],
          }}
          onClose={() => { }}
        />
      </SheetCTAFooter>
    </div>
  );
};
